import {
    Badge,
    Button,
    Combobox,
    Group,
    InputBase,
    Modal,
    NumberInput,
    Stack,
    Text,
    useCombobox,
} from '@mantine/core'
import {useMemo, useState} from 'react'

import {useDispatchYards} from 'src/dispatch/use-dispatch'
import {resourceFitBadgeColor, signalAttentionColor} from 'src/theme/semantic-colors'

import panelClasses from './dispatch-assignment-panel.module.css'
import {boardJobScheduleDateRange} from './dispatch-board-resource-strip'
import {DispatchResourceWeekStrip} from './dispatch-resource-week-strip'
import {ALSO_BOOKED_PREFIX, BORROWED_DRIVER_REASON} from './driver-fit'
import {OUT_OF_SERVICE_BACK_PREFIX} from './equipment-requirement-match'
import type {BookingDays, DispatchTimelineData, JobRequirement, UnassignedJob} from './types'

export type DispatchPickerCandidate = {
    resourceId: string
    resourceName: string
    tier: 'recommended' | 'available' | 'booked'
    fitLabel?: string
    reasons: string[]
    stockQuantity?: boolean
    /** Stock held elsewhere — confirm reassigns quantity from other jobs. */
    stockReassign?: boolean
    /** Holding jobs with quantities for picker/modal context. */
    heldElsewhere?: Array<{jobId: string; jobName: string; quantity: number}>
    /** Holding job IDs known at picker time; refreshed after reassignment. */
    heldElsewhereJobIds?: string[]
    /** Cap for quantity prompts; falls back to picker maxBulkQuantity. */
    maxQuantity?: number
    rentalAssetTypeId?: string
    conflictJob?: {jobId: string; jobName: string}
    conflictRelease?: {date: string; time: string}
    availabilityMessage?: string
}

type DispatchResourcePickerProps = {
    requirement: JobRequirement
    boardJob: UnassignedJob
    candidates: DispatchPickerCandidate[]
    displayData: DispatchTimelineData
    referenceDate: Date
    excludeJobId?: string
    disabled?: boolean
    maxBulkQuantity?: number
    slotIndex?: number
    /** Day range applied to new worker allocations. */
    bookingDays?: BookingDays
    onAssign: (candidate: DispatchPickerCandidate, quantity?: number) => void
    /** Replace the open job drawer with another board job. */
    onOpenBoardJob?: (jobId: string) => void
}

function pickerTestId(requirement: JobRequirement, slotIndex?: number): string {
    if (slotIndex != null) {
        return `dispatch-resource-picker-${requirement.id}-slot-${slotIndex}`
    }
    return `dispatch-resource-picker-${requirement.id}`
}

function pickerPlaceholder(requirement: JobRequirement): string {
    if (requirement.kind !== 'worker') {
        return 'Search equipment…'
    }

    const type = requirement.label.trim().toLowerCase()
    if (!type) {
        return 'Search workers…'
    }

    return `Search ${type.endsWith('s') ? type : `${type}s`}…`
}

function CandidateOptionContent({
    candidate,
    requirement,
    boardJob,
    displayData,
    referenceDate,
    excludeJobId,
    bookingDays,
    onOpenBoardJob,
    borrowedFrom,
}: {
    /** Home yard name of a driver borrowed from another yard. */
    borrowedFrom?: string
    candidate: DispatchPickerCandidate
    requirement: JobRequirement
    boardJob: UnassignedJob
    displayData: DispatchTimelineData
    referenceDate: Date
    excludeJobId?: string
    bookingDays?: BookingDays
    onOpenBoardJob?: (jobId: string) => void
}) {
    const showAvailabilityStrip =
        requirement.kind === 'worker' ||
        (requirement.kind === 'rental_asset' && !candidate.stockQuantity)
    const isBooked = candidate.tier === 'booked'
    const alsoBooked = candidate.reasons.find((reason) => reason.startsWith(ALSO_BOOKED_PREFIX))
    const borrowed = candidate.reasons.includes(BORROWED_DRIVER_REASON)
    const outOfServiceBack = candidate.reasons.find((reason) =>
        reason.startsWith(OUT_OF_SERVICE_BACK_PREFIX),
    )
    const secondaryReasons = candidate.reasons
        .filter(
            (reason) =>
                reason.trim().length > 0 &&
                reason !== alsoBooked &&
                reason !== outOfServiceBack &&
                reason !== BORROWED_DRIVER_REASON,
        )
        .slice(0, candidate.stockReassign ? 5 : 2)
    const jobRange = boardJobScheduleDateRange(boardJob, displayData)
    const conflictHighlightRange = isBooked ? jobRange : null
    const jobDays = bookingDays
        ? {startDate: bookingDays.from, endDate: bookingDays.until}
        : jobRange

    return (
        <Group gap="sm" wrap="nowrap" align="flex-start" justify="space-between" py={2}>
            <Stack gap={2} style={{minWidth: 0, flex: 1}}>
                <Text size="sm" fw={600} lineClamp={1}>
                    {candidate.resourceName}
                </Text>
                {candidate.fitLabel ? (
                    <Badge
                        size="xs"
                        variant="light"
                        color={resourceFitBadgeColor()}
                        w="fit-content"
                    >
                        {candidate.fitLabel}
                    </Badge>
                ) : null}
                {isBooked ? (
                    <Badge size="xs" variant="light" color={signalAttentionColor()} w="fit-content">
                        Conflict
                    </Badge>
                ) : null}
                {alsoBooked ? (
                    <>
                        <Badge
                            size="xs"
                            variant="filled"
                            color="orange"
                            w="fit-content"
                            data-testid="also-booked-badge"
                        >
                            Booked that day
                        </Badge>
                        <Text size="xs" c="orange.8" fw={600} lineClamp={1}>
                            {alsoBooked}
                        </Text>
                    </>
                ) : null}
                {borrowed ? (
                    <Badge
                        size="xs"
                        variant="light"
                        color="grape"
                        w="fit-content"
                        data-testid="borrowed-driver-badge"
                    >
                        {borrowedFrom ? `From ${borrowedFrom}` : 'Other yard'}
                    </Badge>
                ) : null}
                {outOfServiceBack ? (
                    <Badge
                        size="xs"
                        variant="light"
                        color="yellow"
                        w="fit-content"
                        data-testid="out-of-service-back-tag"
                    >
                        {outOfServiceBack}
                    </Badge>
                ) : null}
                {secondaryReasons.map((reason) => (
                    <Text key={reason} size="xs" c="dimmed" lineClamp={1}>
                        {reason}
                    </Text>
                ))}
            </Stack>
            {showAvailabilityStrip ? (
                <div className={panelClasses.pickerAvailabilityColumn}>
                    <DispatchResourceWeekStrip
                        resourceId={candidate.resourceId}
                        displayData={displayData}
                        referenceDate={referenceDate}
                        excludeJobId={excludeJobId}
                        compact
                        conflictHighlightRange={conflictHighlightRange}
                        jobDays={jobDays}
                        onOpenBoardJob={onOpenBoardJob}
                    />
                </div>
            ) : null}
        </Group>
    )
}

function PickerColumnHeaders({showAvailability}: {showAvailability: boolean}) {
    return (
        <Group
            gap="sm"
            wrap="nowrap"
            justify="space-between"
            className={panelClasses.pickerColumnHeader}
            data-testid="picker-column-header"
        >
            <span className={panelClasses.pickerColumnHeaderLabel}>Name</span>
            {showAvailability ? (
                <div className={panelClasses.pickerAvailabilityColumn}>
                    <span className={panelClasses.pickerColumnHeaderLabel}>Availability</span>
                </div>
            ) : null}
        </Group>
    )
}

export function DispatchResourcePicker({
    requirement,
    boardJob,
    candidates,
    displayData,
    referenceDate,
    excludeJobId,
    disabled = false,
    maxBulkQuantity = 1,
    slotIndex,
    bookingDays,
    onAssign,
    onOpenBoardJob,
}: DispatchResourcePickerProps) {
    const combobox = useCombobox({
        onDropdownClose: () => {
            combobox.resetSelectedOption()
            setSearch('')
        },
    })
    const [search, setSearch] = useState('')
    const [pendingStock, setPendingStock] = useState<DispatchPickerCandidate | null>(null)
    const [stockQuantity, setStockQuantity] = useState(1)
    const [pendingBooked, setPendingBooked] = useState<DispatchPickerCandidate | null>(null)
    const [reassignQuantity, setReassignQuantity] = useState(1)
    const {data: yards = []} = useDispatchYards()
    const homeYardNames = useMemo(() => {
        const yardNames = new Map(yards.map((yard) => [yard.id, yard.name]))
        return new Map(
            displayData.resources.map((resource) => [
                resource.id,
                yardNames.get(resource.homeYardId ?? ''),
            ]),
        )
    }, [displayData.resources, yards])

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) {
            return candidates
        }
        return candidates.filter((candidate) => {
            const haystack = [
                candidate.resourceName,
                ...candidate.reasons,
                candidate.conflictJob?.jobName ?? '',
            ]
                .join(' ')
                .toLowerCase()
            return haystack.includes(query)
        })
    }, [candidates, search])

    const recommended = filtered.filter((candidate) => candidate.tier === 'recommended')
    const available = filtered.filter((candidate) => candidate.tier === 'available')
    const booked = filtered.filter((candidate) => candidate.tier === 'booked')
    const assignable = [...recommended, ...available]
    const showAvailabilityColumn = filtered.some(
        (candidate) =>
            requirement.kind === 'worker' ||
            (requirement.kind === 'rental_asset' && !candidate.stockQuantity),
    )

    const pendingStockMax = pendingStock?.stockQuantity
        ? (pendingStock.maxQuantity ?? maxBulkQuantity)
        : 1
    const pendingReassignMax = pendingBooked?.stockReassign
        ? (pendingBooked.maxQuantity ?? maxBulkQuantity)
        : 1

    function renderOptions(groupCandidates: DispatchPickerCandidate[]) {
        return groupCandidates.map((candidate) => (
            <Combobox.Option
                key={candidate.resourceId}
                value={candidate.resourceId}
                data-testid={`picker-option-${candidate.resourceId}`}
            >
                <CandidateOptionContent
                    candidate={candidate}
                    requirement={requirement}
                    boardJob={boardJob}
                    displayData={displayData}
                    referenceDate={referenceDate}
                    excludeJobId={excludeJobId}
                    bookingDays={bookingDays}
                    onOpenBoardJob={onOpenBoardJob}
                    borrowedFrom={homeYardNames.get(candidate.resourceId)}
                />
            </Combobox.Option>
        ))
    }

    return (
        <Stack gap="xs">
            <Combobox
                store={combobox}
                withinPortal
                onOptionSubmit={(value) => {
                    const candidate = candidates.find((entry) => entry.resourceId === value)
                    if (!candidate || disabled) {
                        return
                    }

                    if (candidate.tier === 'booked') {
                        setPendingBooked(candidate)
                        setReassignQuantity(
                            candidate.stockReassign
                                ? Math.max(1, candidate.maxQuantity ?? maxBulkQuantity)
                                : 1,
                        )
                        setSearch('')
                        combobox.closeDropdown()
                        return
                    }

                    if (candidate.stockQuantity) {
                        const stockMax = candidate.maxQuantity ?? maxBulkQuantity
                        if (stockMax <= 0) {
                            return
                        }
                        setPendingStock(candidate)
                        setStockQuantity(stockMax)
                        setSearch('')
                        combobox.closeDropdown()
                        return
                    }

                    onAssign(candidate)
                    setSearch('')
                    combobox.closeDropdown()
                }}
            >
                <Combobox.Target>
                    <InputBase
                        data-testid={pickerTestId(requirement, slotIndex)}
                        placeholder={pickerPlaceholder(requirement)}
                        value={search}
                        disabled={disabled}
                        onChange={(event) => {
                            setSearch(event.currentTarget.value)
                            combobox.openDropdown()
                        }}
                        onClick={() => combobox.openDropdown()}
                        onFocus={() => combobox.openDropdown()}
                        rightSection={<Combobox.Chevron />}
                        rightSectionPointerEvents="none"
                    />
                </Combobox.Target>

                <Combobox.Dropdown>
                    <Combobox.Options mah={360} style={{overflowY: 'auto'}}>
                        {filtered.length > 0 ? (
                            <PickerColumnHeaders showAvailability={showAvailabilityColumn} />
                        ) : null}
                        {renderOptions(assignable)}
                        {renderOptions(booked)}
                        {filtered.length === 0 ? <Combobox.Empty>No matches</Combobox.Empty> : null}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>

            {pendingStock ? (
                <Stack gap="xs" data-testid={`bulk-assign-${requirement.id}`}>
                    <Text size="sm">How many {pendingStock.resourceName}?</Text>
                    <Group align="flex-end" wrap="nowrap">
                        <NumberInput
                            data-testid={`bulk-quantity-${requirement.id}`}
                            label="Quantity"
                            value={stockQuantity}
                            min={1}
                            max={pendingStockMax}
                            disabled={disabled}
                            error={stockQuantity > pendingStockMax}
                            onChange={(value) => {
                                const next = typeof value === 'number' ? value : Number(value)
                                setStockQuantity(Number.isFinite(next) ? next : 1)
                            }}
                            style={{flex: 1}}
                        />
                        <Button
                            data-testid={`bulk-confirm-${requirement.id}`}
                            disabled={
                                disabled || stockQuantity < 1 || stockQuantity > pendingStockMax
                            }
                            onClick={() => {
                                onAssign(pendingStock, stockQuantity)
                                setPendingStock(null)
                                setStockQuantity(1)
                            }}
                        >
                            Add
                        </Button>
                        <Button
                            variant="default"
                            disabled={disabled}
                            onClick={() => {
                                setPendingStock(null)
                                setStockQuantity(1)
                            }}
                        >
                            Cancel
                        </Button>
                    </Group>
                    {stockQuantity > pendingStockMax ? (
                        <Text size="xs" c="red">
                            Only {pendingStockMax} can be assigned here.
                        </Text>
                    ) : (
                        <Text size="xs" c="dimmed">
                            Up to {pendingStockMax} available to assign.
                        </Text>
                    )}
                </Stack>
            ) : null}

            <Modal
                opened={pendingBooked != null}
                onClose={() => setPendingBooked(null)}
                title={
                    pendingBooked?.stockReassign
                        ? 'Reassign held stock?'
                        : 'Reassign booked resource?'
                }
                centered
                data-testid={pendingBooked != null ? 'booked-reassign-confirm' : undefined}
            >
                {pendingBooked ? (
                    <Stack gap="md">
                        <Text size="sm">
                            {pendingBooked.stockReassign ? (
                                <>
                                    Reassigning will remove {pendingBooked.resourceName} units from
                                    the jobs below and assign them here.
                                </>
                            ) : (
                                <>
                                    {pendingBooked.resourceName}
                                    {pendingBooked.conflictJob
                                        ? ` is on ${pendingBooked.conflictJob.jobName}`
                                        : ' is booked on another job'}
                                    . Reassigning will remove them from that job and assign them
                                    here.
                                </>
                            )}
                        </Text>
                        {pendingBooked.stockReassign &&
                        (pendingBooked.heldElsewhere?.length ?? 0) > 0 ? (
                            <Stack gap={4} data-testid="held-elsewhere-breakdown">
                                {pendingBooked.heldElsewhere!.map((held) => (
                                    <Group
                                        key={held.jobId}
                                        justify="space-between"
                                        gap="sm"
                                        wrap="nowrap"
                                    >
                                        <Text size="sm" lineClamp={1} style={{minWidth: 0}}>
                                            {held.jobName}
                                        </Text>
                                        <Text size="sm" c="dimmed" style={{flexShrink: 0}}>
                                            × {held.quantity}
                                        </Text>
                                    </Group>
                                ))}
                            </Stack>
                        ) : null}
                        {pendingBooked.stockReassign ? (
                            <NumberInput
                                data-testid={`reassign-quantity-${requirement.id}`}
                                label="Quantity"
                                value={reassignQuantity}
                                min={1}
                                max={pendingReassignMax}
                                disabled={disabled || pendingReassignMax < 1}
                                onChange={(value) => {
                                    const next = typeof value === 'number' ? value : Number(value)
                                    setReassignQuantity(Number.isFinite(next) ? next : 1)
                                }}
                            />
                        ) : null}
                        <Group justify="flex-end" gap="xs">
                            {pendingBooked.conflictJob && onOpenBoardJob ? (
                                <Button
                                    variant="default"
                                    data-testid="booked-open-conflict-job"
                                    onClick={() => {
                                        const jobId = pendingBooked.conflictJob!.jobId
                                        setPendingBooked(null)
                                        onOpenBoardJob(jobId)
                                    }}
                                >
                                    Open other job
                                </Button>
                            ) : null}
                            <Button variant="default" onClick={() => setPendingBooked(null)}>
                                Cancel
                            </Button>
                            <Button
                                color={signalAttentionColor()}
                                data-testid="booked-confirm-assign"
                                disabled={
                                    disabled ||
                                    (Boolean(pendingBooked.stockReassign) &&
                                        (reassignQuantity < 1 || pendingReassignMax < 1))
                                }
                                onClick={() => {
                                    if (pendingBooked.stockReassign) {
                                        onAssign(pendingBooked, reassignQuantity)
                                    } else {
                                        onAssign(pendingBooked)
                                    }
                                    setPendingBooked(null)
                                    setReassignQuantity(1)
                                }}
                            >
                                Reassign
                            </Button>
                        </Group>
                    </Stack>
                ) : null}
            </Modal>
        </Stack>
    )
}
